import { Link } from 'react-router-dom';
import { scenes } from '@/mocks/scenes';
import { formatHeat } from '@/utils/format';

type Scene = (typeof scenes)[number];

type SceneCardProps = {
  scene: Scene;
  rank?: number;
  layout?: 'vertical' | 'horizontal';
};

export default function SceneCard({ scene, rank, layout = 'vertical' }: SceneCardProps) {
  if (layout === 'horizontal') {
    return (
      <article className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300">
        <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded-md md:h-28 md:w-40">
          <img
            src={scene.cover}
            alt={`${scene.title} 封面图`}
            title={`${scene.title} 玩梗名场面封面`}
            className="h-full w-full object-top"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">
              {scene.categoryName}
            </span>
            <span>热度 {formatHeat(scene.heat)}</span>
          </div>
          <h3 className="mt-1.5 line-clamp-2 text-base text-foreground-950">
            <Link to={`/scene/${scene.id}`} className="transition-colors hover:text-primary-600">
              {scene.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">
            {scene.excerpt}
          </p>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <div className="relative h-44 w-full overflow-hidden md:h-48">
        <img
          src={scene.cover}
          alt={`${scene.title} 封面图`}
          title={`${scene.title} 玩梗名场面封面`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <span className="absolute left-3 top-3 rounded-full bg-foreground-950/75 px-2.5 py-1 text-xs text-background-50">
          {scene.categoryName}
        </span>
        {typeof rank === 'number' && (
          <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 font-heading text-lg text-secondary-950">
            {rank}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="text-base leading-snug text-foreground-950 md:text-lg">
          <Link to={`/scene/${scene.id}`} className="transition-colors hover:text-primary-600">
            {scene.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {scene.excerpt}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {scene.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-background-200 px-2.5 py-1 text-xs text-foreground-700"
            >
              #{tag}
            </span>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-fire-line text-primary-500" aria-hidden="true" />
            热度 {formatHeat(scene.heat)}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line" aria-hidden="true" />
              {formatHeat(scene.comments)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-movie-2-line" aria-hidden="true" />
              {formatHeat(scene.danmaku)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}